// ============================================================
// STARTUP DIAGNOSTICS — Fase 11.9
// System integrity checks ao iniciar a aplicação
// Detecta: DB connectivity, realtime, FF, stores, env vars
// ============================================================
import { supabase } from '@/lib/supabase'
import { obs } from '@/core/observability'
import { checkStreamHealth } from '@/core/eventStreamProvider'
import { getFlags } from '@/core/featureFlags'

export interface DiagnosticResult {
  check:    string
  status:   'ok' | 'warn' | 'fail'
  message:  string
  latencyMs?: number
}

export interface StartupReport {
  timestamp:   string
  allOk:       boolean
  checks:      DiagnosticResult[]
  environment: string
  version:     string
}

// ─── Run all startup checks ───────────────────────────────────
export async function runStartupDiagnostics(): Promise<StartupReport> {
  const t0      = Date.now()
  const checks: DiagnosticResult[] = []

  obs.info('startup', 'Running startup diagnostics…')

  // 1. DB connectivity
  checks.push(await checkDBConnectivity())

  // 2. Realtime
  checks.push(await checkRealtimeConnectivity())

  // 3. Auth
  checks.push(await checkAuth())

  // 4. Feature flags
  checks.push(checkFeatureFlags())

  // 5. Browser APIs
  checks.push(checkBrowserAPIs())

  // 6. Environment
  checks.push(checkEnvironment())

  const allOk    = checks.every(c => c.status !== 'fail')
  const fails    = checks.filter(c => c.status === 'fail')
  const warnings = checks.filter(c => c.status === 'warn')

  const report: StartupReport = {
    timestamp:   new Date().toISOString(),
    allOk,
    checks,
    environment: import.meta.env.PROD ? 'production' : 'development',
    version:     import.meta.env.VITE_APP_VERSION ?? 'unknown',
  }

  const totalMs = Date.now() - t0

  if (fails.length > 0) {
    obs.error('startup', `Startup failed: ${fails.map(f => f.check).join(', ')}`, {
      fails:    fails.length,
      warnings: warnings.length,
      totalMs,
    })
  } else if (warnings.length > 0) {
    obs.warn('startup', `Startup OK with warnings: ${warnings.map(w => w.check).join(', ')}`, {
      warnings: warnings.length,
      totalMs,
    })
  } else {
    obs.info('startup', `Startup OK in ${totalMs}ms`)
  }

  return report
}

// ─── Individual checks ─────────────────────────────────────────
async function checkDBConnectivity(): Promise<DiagnosticResult> {
  const t0 = Date.now()
  try {
    const { error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1)
      .maybeSingle()

    if (error && error.code !== 'PGRST116') {  // PGRST116 = no rows (OK)
      return { check: 'db_connectivity', status: 'warn', message: `DB query warning: ${error.message}`, latencyMs: Date.now() - t0 }
    }
    return { check: 'db_connectivity', status: 'ok', message: 'Database reachable', latencyMs: Date.now() - t0 }
  } catch (err) {
    return { check: 'db_connectivity', status: 'fail', message: `DB unreachable: ${String(err)}`, latencyMs: Date.now() - t0 }
  }
}

async function checkRealtimeConnectivity(): Promise<DiagnosticResult> {
  try {
    const result = await checkStreamHealth()
    return {
      check:     'realtime',
      status:    result.ok ? 'ok' : 'warn',
      message:   result.ok ? `Realtime OK (${result.latencyMs}ms)` : 'Realtime unreachable',
      latencyMs: result.latencyMs,
    }
  } catch (err) {
    return { check: 'realtime', status: 'warn', message: `Realtime check failed: ${String(err)}` }
  }
}

async function checkAuth(): Promise<DiagnosticResult> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    return {
      check:   'auth',
      status:  session ? 'ok' : 'warn',
      message: session ? 'Authenticated session active' : 'No active session (unauthenticated)',
    }
  } catch (err) {
    return { check: 'auth', status: 'fail', message: `Auth check failed: ${String(err)}` }
  }
}

function checkFeatureFlags(): DiagnosticResult {
  try {
    const flags = getFlags()
    const activeCount = Object.values(flags).filter(Boolean).length
    return {
      check:   'feature_flags',
      status:  'ok',
      message: `Feature flags loaded (${activeCount} active)`,
    }
  } catch (err) {
    return {
      check:   'feature_flags',
      status:  'warn',
      message: `Feature flags failed, using defaults: ${String(err)}`,
    }
  }
}

function checkBrowserAPIs(): DiagnosticResult {
  const missing: string[] = []

  if (typeof ResizeObserver === 'undefined') missing.push('ResizeObserver')
  if (typeof IntersectionObserver === 'undefined') missing.push('IntersectionObserver')
  if (typeof AbortController === 'undefined') missing.push('AbortController')

  // BarcodeDetector is optional (OCR feature)
  const hasBarcodeDetector = 'BarcodeDetector' in window

  if (missing.length > 0) {
    return {
      check:   'browser_apis',
      status:  'warn',
      message: `Missing browser APIs: ${missing.join(', ')}`,
    }
  }

  return {
    check:   'browser_apis',
    status:  'ok',
    message: `Browser APIs OK. BarcodeDetector: ${hasBarcodeDetector ? 'available' : 'unavailable (OCR fallback)'}`,
  }
}

function checkEnvironment(): DiagnosticResult {
  const required = ['VITE_SUPABASE_URL']
  const missing  = required.filter(k => !import.meta.env[k])
  const hasPublishableKey = Boolean(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY)

  if (!hasPublishableKey) missing.push('VITE_SUPABASE_PUBLISHABLE_KEY')

  if (missing.length > 0) {
    return {
      check:   'environment',
      status:  'fail',
      message: `Missing required env vars: ${missing.join(', ')}`,
    }
  }

  return {
    check:   'environment',
    status:  'ok',
    message: `Environment vars present (${required.length}/${required.length})`,
  }
}
